<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { router } from '../lib/router.svelte.ts';
  import { describeScan, type ScanMessage } from '../lib/scan/outcome.ts';
  import { chime, haptic, primeAudio } from '../lib/feedback.ts';
  import { roundBadge } from '../lib/format.ts';
  import { normaliseScanInput, type RoundProgress } from '@chariot/contract';
  import Scanner from '../lib/Scanner.svelte';
  import CorrectionSheet from '../lib/CorrectionSheet.svelte';

  let scannerOpen = $state(false);
  let feedback = $state<ScanMessage | null>(null);
  let feedbackAt = $state<string | null>(null);
  let chosenSite = $state('');
  let manualOpen = $state(false);
  let manualCart = $state('');
  let closing = $state(false);
  let confirmClose = $state(false);
  let closeResult = $state<RoundProgress | null>(null);
  let closedRoundId = $state<string | null>(null);
  let confirmAbandon = $state(false);
  let list = $state<'missing' | 'found' | 'arrived'>('missing');
  let correcting = $state(false);

  const round = $derived(store.activeRound);
  const site = $derived(round ? store.siteByCode.get(round.site_code) : null);
  const progress = $derived(store.progressForActiveRound());
  const activeSites = $derived(store.sites.filter((s) => s.status === 'active'));
  const duplicates = $derived(round ? store.otherRoundsHere(round.site_code) : []);
  const recentRounds = $derived(store.rounds.slice(0, 5));

  /** The one line the scanner keeps on screen: where the sweep stands right now. */
  const status = $derived(
    progress
      ? `${progress.scanned.length} scanné${progress.scanned.length === 1 ? '' : 's'} · ${progress.missing.length} restants`
      : '',
  );

  const say = (o: Parameters<typeof describeScan>[0]) =>
    describeScan(o, (c) => store.siteName(c));

  function show(msg: ScanMessage) {
    feedback = msg;
    feedbackAt = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return msg;
  }

  /** From the camera: the scanner itself holds the result and plays the cues. */
  async function onScan(text: string) {
    return show(say(await store.handleScan(text)));
  }

  /** From a keyboard or a tap: the same cues, since the same fact was recorded. */
  function announce(msg: ScanMessage) {
    haptic(msg.tone);
    if (store.scanSound) chime(msg.tone);
    return show(msg);
  }

  async function startRound() {
    if (!chosenSite) return;
    primeAudio();
    announce(say(await store.openRound(chosenSite)));
    chosenSite = '';
  }

  /**
   * Typing a code by hand. Not a convenience: with a couple of hundred printed labels on
   * carts that get knocked about, some end up torn or smudged, and without this there is no
   * way to record such a cart at all. Goes through the same path as a camera read, so the
   * rules and the messages cannot drift apart.
   */
  async function submitManualCart() {
    if (!manualCart.trim()) return;
    primeAudio();
    announce(say(await store.handleScan(normaliseScanInput(manualCart))));
    manualCart = '';
  }

  async function doClose() {
    confirmClose = false;
    closing = true;
    closedRoundId = round?.round_id ?? null;
    try {
      closeResult = await store.closeRound();
    } finally {
      closing = false;
    }
  }

  const label = (code: string) => store.cartByCode.get(code)?.name ?? code;
  const hhmm = (iso: string) =>
    new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
</script>

{#if !round}
  <h1>Contrôle</h1>
  <div class="card narrow">
    <label class="field">
      <span>Sur quel site êtes-vous ?</span>
      <select bind:value={chosenSite}>
        <option value="">— choisir un site —</option>
        {#each activeSites as s (s.code)}
          <option value={s.code}>{s.name}</option>
        {/each}
      </select>
    </label>
    <button class="primary block big" onclick={startRound} disabled={!chosenSite}>
      Démarrer la tournée
    </button>
    <button class="ghost block slim" style="margin-top:.4rem" onclick={() => (correcting = true)}>
      J'ai retrouvé un chariot — l'enregistrer seul
    </button>
    {#if activeSites.length === 0}
      <div class="banner warn" style="margin-top:.8rem">
        Aucun site enregistré. Créez-en un depuis <a href="#/sites">Sites</a>.
      </div>
    {/if}
  </div>

  {#if feedback}
    <div class="banner {feedback.tone === 'neutral' ? 'info' : feedback.tone}">{feedback.text}</div>
  {/if}

  {#if recentRounds.length > 0}
    <div class="card">
      <h2>Dernières tournées</h2>
      <ul class="list">
        {#each recentRounds as r (r.round_id)}
          {@const b = roundBadge(r)}
          <li>
            <button class="row-link" onclick={() => router.go(`rounds/${r.round_id}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="truncate row-title">{store.siteName(r.site_code)}</span>
                <span class="tiny muted">{new Date(r.opened_at).toLocaleString('fr-FR')}</span>
              </span>
            </button>
            <span class="pill">{b.count}</span>
            <span class="pill {b.tone}">{b.status}</span>
          </li>
        {/each}
      </ul>
    </div>
  {/if}
{:else}
  <div class="row" style="margin-bottom:.1rem">
    <h1 class="grow truncate">{site?.name ?? round.site_code}</h1>
    <span class="pill accent">depuis {hhmm(round.opened_at)}</span>
  </div>

  {#if duplicates.length > 0}
    <div class="banner warn">Un autre relevé est en cours ici. Les deux sont gardés.</div>
  {/if}

  <div class="stat-row" style="margin-top:.6rem">
    <div class="stat is-ok">
      <div class="k">trouvés</div>
      <div class="n">{progress?.found.length ?? 0}</div>
      <div class="sub">sur {progress?.expected.length ?? 0}</div>
    </div>
    <div class="stat" class:is-warn={(progress?.missing.length ?? 0) > 0}>
      <div class="k">reste</div>
      <div class="n">{progress?.missing.length ?? 0}</div>
    </div>
    <div class="stat">
      <div class="k">d'ailleurs</div>
      <div class="n">{progress?.arrived.length ?? 0}</div>
    </div>
  </div>

  <button class="primary block big" onclick={() => { primeAudio(); scannerOpen = true; }}>
    Scanner un chariot
  </button>

  {#if feedback}
    <div class="banner {feedback.tone === 'neutral' ? 'info' : feedback.tone}" style="margin-top:.6rem">
      <span class="grow">{feedback.text}</span>
      {#if feedbackAt}<span class="tiny subtle">{feedbackAt}</span>{/if}
    </div>
  {/if}

  {#if manualOpen}
    <form class="manual-entry" onsubmit={(e) => { e.preventDefault(); void submitManualCart(); }}>
      <input
        bind:value={manualCart}
        placeholder="CHT-000123"
        aria-label="Saisir un code de chariot"
        autocapitalize="characters"
        autocomplete="off"
        spellcheck="false"
      />
      <button type="submit" disabled={!manualCart.trim()}>Ajouter</button>
    </form>
  {:else}
    <button class="ghost slim" style="margin-top:.4rem" onclick={() => (manualOpen = true)}>
      Étiquette illisible — saisir le code
    </button>
  {/if}

  <div class="segmented" role="group" aria-label="Chariots de la tournée" style="margin-top:.9rem">
    <button aria-pressed={list === 'missing'} onclick={() => (list = 'missing')}>
      Reste {progress?.missing.length ?? 0}
    </button>
    <button aria-pressed={list === 'found'} onclick={() => (list = 'found')}>
      Trouvés {progress?.found.length ?? 0}
    </button>
    <button aria-pressed={list === 'arrived'} onclick={() => (list = 'arrived')}>
      D'ailleurs {progress?.arrived.length ?? 0}
    </button>
  </div>

  <div class="card">
    {#if list === 'missing'}
      {#if !progress || progress.missing.length === 0}
        <p class="empty">Rien ne manque ici.</p>
      {:else}
        <ul class="list">
          {#each progress.missing as code (code)}
            <li>
              <span class="grow truncate row-title">{label(code)}</span>
              <span class="mono tiny subtle">{code}</span>
            </li>
          {/each}
        </ul>
      {/if}
    {:else if list === 'found'}
      {#if !progress || progress.found.length === 0}
        <p class="empty">Aucun chariot scanné.</p>
      {:else}
        <ul class="list">
          {#each progress.found as code (code)}
            <li>
              <span class="grow truncate row-title">{label(code)}</span>
              <span class="mono tiny subtle">{code}</span>
            </li>
          {/each}
        </ul>
      {/if}
    {:else if !progress || progress.arrived.length === 0}
      <p class="empty">Aucun chariot venu d'un autre site.</p>
    {:else}
      <ul class="list">
        {#each progress.arrived as code (code)}
          <li>
            <span class="stack grow" style="gap:.1rem">
              <span class="truncate row-title">{label(code)}</span>
              <span class="tiny muted">
                {progress.arrivedFrom[code]
                  ? `depuis ${store.siteName(progress.arrivedFrom[code])}`
                  : 'jamais localisé'}
              </span>
            </span>
            <span class="mono tiny subtle">{code}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  <div class="row wrap" style="margin-top:.9rem">
    <button
      class="ok grow"
      disabled={closing}
      onclick={() => ((progress?.missing.length ?? 0) > 0 ? (confirmClose = true) : doClose())}
    >
      {#if closing}<span class="spinner"></span>{/if} Terminer la tournée
    </button>
    <button class="ghost" onclick={() => (confirmAbandon = true)}>Interrompre</button>
  </div>
{/if}

{#if correcting}
  <CorrectionSheet site_code={null} onclose={() => (correcting = false)} />
{/if}

{#if scannerOpen}
  <Scanner
    title={site?.name ?? round?.site_code ?? 'Scanner'}
    hint="Visez le QR du chariot"
    forceFallback={store.forceFallbackScanner}
    holdMs={store.scanHoldMs}
    sound={store.scanSound}
    {status}
    onresult={onScan}
    onclose={() => (scannerOpen = false)}
  />
{/if}

{#if confirmClose}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Terminer la tournée">
    <div class="sheet">
      <h2>Terminer ?</h2>
      <p class="small muted">
        {progress?.missing.length} chariot{(progress?.missing.length ?? 0) === 1 ? '' : 's'}
        {(progress?.missing.length ?? 0) === 1 ? 'sera déclaré manquant' : 'seront déclarés manquants'}.
        Une tournée terminée ne se rouvre pas.
      </p>
      <div class="row">
        <button class="ok grow" onclick={doClose}>Terminer</button>
        <button class="ghost" onclick={() => (confirmClose = false)}>Continuer la tournée</button>
      </div>
    </div>
  </div>
{/if}

{#if closeResult}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Tournée terminée">
    <div class="sheet">
      <h2>Tournée terminée</h2>
      <p class="small muted">
        {closeResult.found.length} trouvé{closeResult.found.length === 1 ? '' : 's'} sur
        {closeResult.expected.length} attendu{closeResult.expected.length === 1 ? '' : 's'}{#if closeResult.arrived.length > 0}, {closeResult.arrived.length} venu{closeResult.arrived.length === 1 ? '' : 's'} d'ailleurs{/if}.
      </p>

      {#if closeResult.missing.length === 0}
        <div class="banner ok">Tout ce qui était attendu ici a été trouvé.</div>
      {:else}
        <div class="banner warn">
          {closeResult.missing.length} déclaré{closeResult.missing.length === 1 ? '' : 's'} manquant{closeResult.missing.length === 1 ? '' : 's'}.
          Un scan ultérieur, n'importe où, lève le signalement.
        </div>
        <ul class="list">
          {#each closeResult.missing as code (code)}
            <li>
              <span class="grow truncate row-title">{label(code)}</span>
              <span class="mono tiny subtle">{code}</span>
            </li>
          {/each}
        </ul>
      {/if}

      <div class="row wrap" style="margin-top:.9rem">
        <button class="primary grow" onclick={() => { closeResult = null; }}>Terminé</button>
        {#if closedRoundId}
          <button class="ghost" onclick={() => { closeResult = null; router.go(`rounds/${closedRoundId}`); }}>
            Voir le détail
          </button>
        {/if}
      </div>
    </div>
  </div>
{/if}

{#if confirmAbandon}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Interrompre la tournée">
    <div class="sheet">
      <h2>Interrompre la tournée ?</h2>
      <p class="small muted">
        Vos {round?.scanned.length ?? 0} scan{(round?.scanned.length ?? 0) === 1 ? '' : 's'}
        {(round?.scanned.length ?? 0) === 1 ? 'est gardé' : 'sont gardés'}. Aucun chariot ne sera
        déclaré manquant : un tour incomplet ne prouve pas qu'un chariot a disparu.
      </p>
      <div class="row">
        <button class="danger grow" onclick={async () => { confirmAbandon = false; await store.abandonRound(); }}>
          Interrompre
        </button>
        <button class="ghost" onclick={() => (confirmAbandon = false)}>Annuler</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .manual-entry { display: flex; gap: .5rem; margin-top: .6rem; }
  .manual-entry input { font-family: var(--mono); }
  .manual-entry button { flex: none; }
</style>
